import _ from 'lodash'
import { AllState } from '../../../store'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import { PageArea, PageProps } from '..'
import { BulkSheetView } from '../../containers/BulkSheetView'
import Loading from '../../components/process-state-notifications/Loading'
import { DeleteRowConfirmationDialog } from '../../containers/BulkSheetView/components/dialog/DeleteRowConfirmationDialog'
import { intl } from '../../../i18n'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChatChannelMessageTicketCreationMappingRow } from './chatChannelMessageTicketCreationMapping'
import { ChatChannelMessageTicketCreationMappingGridOptions } from './gridOptions'
import { InputError } from '../../containers/BulkSheetView/lib/validation'
import {
  GridApi,
  GridReadyEvent,
  GetContextMenuItemsParams,
  RowDataUpdatedEvent,
} from 'ag-grid-community'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { useDialog } from './hooks/dialog'
import { addRowMenuItems, deleteRowMenuItems } from './gridOptions/contextMenu'
import {
  MessageLevel,
  addGlobalMessage,
  addScreenMessage,
} from '../../../store/messages'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'
import { removeRows } from '../../containers/BulkSheetView/hooks/actions/crudTreeRows'
import { useKeyBind } from '../../hooks/useKeyBind'
import { KEY_SAVE } from '../../model/keyBind'
import { useChatChannelMessageTicketCreationMappingsData } from './hooks/data'
import { useDispatch, useSelector } from 'react-redux'
import { WbsItemTypeVO } from '../../../domain/value-object/WbsItemTypeVO'
import Header from './components/Header'
import { useSlackExternalServiceStorageService } from '../../../services/storage-services/external-services/slackExternalServiceStorageService'
import { SlackWorkspace } from '../../../domain/value-object/Slack/WorkspaceVO'
import { useGridContext } from '../../containers/BulkSheetView/hooks/useGridContext'

type WrapperProps = PageProps

export const ChatChannelMessageTicketCreationMapping = projectPrivate(
  ({ uuid: functionUuid }: WrapperProps) => {
    const { project } = useProjectPrivateContext()
    const [loading, setLoading] = useState<boolean>(true)
    const dispatch = useDispatch()
    const [initializedSelectOptions, setInitializedSelectOptions] =
      useState<boolean>(false)
    const [authorizedSlackWorkspace, setAuthorizedSlackWorkspace] =
      useState<SlackWorkspace>()
    const ref = useRef<HTMLDivElement>(null)
    const gridApiRef = useRef<GridApi>()
    const [isGridReady, setIsGridReady] = useState(false)

    // Initialize the authorized workspace.
    const { getAuthorizedSlackWorkspace, getChannelsFromSlackApi } =
      useSlackExternalServiceStorageService()
    useEffect(() => {
      getAuthorizedSlackWorkspace().then(slackWorkspaces => {
        if (slackWorkspaces?.length > 0) {
          const authorizedSlackWorkspace = slackWorkspaces[0]
          setAuthorizedSlackWorkspace(authorizedSlackWorkspace)
        } else {
          dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({
                id: 'slackWorkspaceIsNotAuthorized.title',
              }),
              text: intl.formatMessage({
                id: 'slackWorkspaceIsNotAuthorized.message',
              }),
            })
          )
        }
      })
    }, [])

    const { data, setData, refresh, save, deleteRows, hasDuplicatedData } =
      useChatChannelMessageTicketCreationMappingsData(
        project.uuid,
        authorizedSlackWorkspace?.slackWorkspaceId
      )

    const gridOptions = useMemo(() => {
      return ChatChannelMessageTicketCreationMappingGridOptions()
    }, [])
    const { contextRef, updateContext } = useGridContext(
      gridApiRef.current,
      gridOptions.context
    )
    const ticketTypes = useSelector<AllState, WbsItemTypeVO[]>(
      state => state.project.ticketTypes
    )

    useEffect(() => {
      updateContext({
        ticketType: ticketTypes,
      })
      setInitializedSelectOptions(true)
    }, [ticketTypes, updateContext])

    const submit = useRef<Function>()
    submit.current = save
    const reload = useRef<Function>()
    reload.current = refresh

    const refreshAll = useCallback(async () => {
      if (!gridApiRef.current) return
      setLoading(true)
      try {
        const refreshRows = reload.current ?? refresh
        await refreshRows(project.uuid)
      } finally {
        updateContext({
          errors: new InputError(),
        })
        setLoading(false)
      }
    }, [gridOptions, project.uuid, refresh, updateContext])

    const onSubmit = useCallback(async () => {
      setLoading(true)
      try {
        gridApiRef.current?.stopEditing()
        if (contextRef.current.errors.hasMessage()) {
          dispatch(
            addGlobalMessage({
              type: MessageLevel.WARN,
              title: intl.formatMessage({ id: 'global.warning.businessError' }),
              text: contextRef.current.errors.toMessage(id => {
                const node = gridApiRef.current?.getRowNode(id)
                return !!node
                  ? (
                      data.findIndex(v => v.uuid === node.data.uuid) + 1
                    ).toString()
                  : ''
              }),
            })
          )
          return
        }
        if (hasDuplicatedData()) {
          dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.WARN,
              title: intl.formatMessage({
                id: 'chatChannelMessageTicketCreationMapping.validation.message.duplicatedRows',
              }),
            })
          )
          return
        }

        const response = submit.current ? await submit.current() : await save()
        if (!response.hasError && !response.hasWarning) {
          dispatch(
            addScreenMessage(functionUuid, {
              type: MessageLevel.SUCCESS,
              title: intl.formatMessage({ id: 'registration.complete' }),
            })
          )
          await refreshAll()
        }
      } finally {
        setLoading(false)
      }
    }, [data, functionUuid, hasDuplicatedData, refreshAll, save, dispatch])

    const hasChanged = useCallback((): boolean => {
      return (prevData.current || data).some(v => v.added || v.edited)
    }, [data])

    const dialog = useDialog()

    const onReload = useCallback(() => {
      if (hasChanged()) {
        dialog.openCancel()
      } else {
        refreshAll()
      }
    }, [dialog, hasChanged, refreshAll])

    const onDeleteRows = useCallback(() => {
      const rows = getSelectedNode(gridApiRef.current!).map(
        v => v.data
      ) as ChatChannelMessageTicketCreationMappingRow[]
      const target = _.uniqBy(rows, 'uuid')
      const newData = removeRows(data, target)
      if (contextRef.current.errors) {
        target.forEach(v => contextRef.current.errors.removeErrorsById(v.uuid))
      }
      setData(newData)
      deleteRows(target)
      dialog.closeDeleteConfirmation()
    }, [data, deleteRows, dialog, setData])

    const bulkSheetState = useBulkSheetState(functionUuid, project.uuid)

    const prevData = useRef<ChatChannelMessageTicketCreationMappingRow[]>([])
    prevData.current = data

    const contextMenu = useCallback(
      (params: GetContextMenuItemsParams) => {
        const selectedNodes = getSelectedNode(params.api)
        if (!params.node) return []
        const items = [
          ...addRowMenuItems(params, data, setData, project.uuid),
          ...deleteRowMenuItems(selectedNodes, dialog.openDeleteConfirmation),
        ]
        return items
      },
      [data, dialog.openDeleteConfirmation, project.uuid, setData]
    )

    useEffect(() => {
      if (!initializedSelectOptions || !bulkSheetState.initialized) {
        return
      }
      refreshAll()
    }, [refreshAll, initializedSelectOptions, bulkSheetState.initialized])

    useKeyBind(
      [{ key: KEY_SAVE, fn: onSubmit, stopDefaultBehavior: true }],
      [onSubmit]
    )

    return (
      <PageArea>
        <Header loading={loading} onSubmit={onSubmit} onReload={onReload} />
        <BulkSheetView
          ref={ref}
          gridOptions={gridOptions}
          rowData={data}
          getContextMenuItems={contextMenu}
          onGridReady={(e: GridReadyEvent) => {
            gridApiRef.current = e.api
            setIsGridReady(true)
          }}
          onRowDataUpdated={(
            e: RowDataUpdatedEvent<ChatChannelMessageTicketCreationMappingRow>
          ) => e.api?.refreshCells({ columns: ['rowNumber'], force: true })}
        />
        <Loading isLoading={loading} elem={ref.current} />
        {dialog.deleteConfirmation && isGridReady && (
          <DeleteRowConfirmationDialog
            target={getSelectedNode(gridApiRef.current!).map(v => {
              const row = v.data as ChatChannelMessageTicketCreationMappingRow
              const chatChannelLabel = row.chatChannel
                ? row.chatChannel.slackChannelName
                : intl.formatMessage({
                    id: 'chatChannelMessageTicketCreationMapping.dialog.deleteRowsConfirmation.message.chatChannel.undefined',
                  })
              const ticketTypeLabel = row.ticketType
                ? row.ticketType.getNameWithSuffix()
                : intl.formatMessage({
                    id: 'chatChannelMessageTicketCreationMapping.dialog.deleteRowsConfirmation.message.ticketType.undefined',
                  })
              const ticketListLabel = row.ticketList
                ? row.ticketList.displayName
                : intl.formatMessage({
                    id: 'chatChannelMessageTicketCreationMapping.dialog.deleteRowsConfirmation.message.ticketList.undefined',
                  })
              return `[${chatChannelLabel}]-[${ticketTypeLabel}]-[${ticketListLabel}]`
            })}
            onConfirm={onDeleteRows}
            onClose={dialog.closeDeleteConfirmation}
          />
        )}
        {dialog.cancel && (
          <CancelConfirmDialog
            open={true}
            onConfirm={() => {
              dialog.closeCancel()
              refreshAll()
            }}
            onClose={dialog.closeCancel}
          />
        )}
      </PageArea>
    )
  }
)
